// Invio HTTP fire-and-forget. Non solleva mai (la telemetria non deve crashare
// l'host) e non ha retry loop.
//
// Browser: `fetch` con `keepalive: true` (solo sotto la quota body di 64KB di
// Chrome) così le richieste sopravvivono all'unload della pagina. NON usiamo
// `navigator.sendBeacon` — non può impostare l'header `Authorization: Bearer`.
// Solo `Authorization` + `Content-Type` come header: nessun header custom, per
// non ampliare la preflight CORS. I redirect restano al fetch nativo (`follow`):
// con `manual` la risposta sarebbe `opaqueredirect` senza Location leggibile.
//
// Node: `redirect:'manual'` + follow a mano (max 2) ri-POSTando lo stesso body —
// un 301 apex→www con `follow` degraderebbe il POST in GET (parità con il
// Transport ruby/dart, MAX_REDIRECTS=2). In più viaggia lo User-Agent
// `closeyourit-js/{versione}` (nel browser è vietato dal fetch spec).

import { isBrowser, isNode } from './runtime'
import type { Stats } from './stats'
import { SDK_NAME, SDK_VERSION } from './version'

type FetchImpl = typeof fetch

const MAX_REDIRECTS = 2
// Quota keepalive di Chrome: 64KB per-origin sui body in volo. Oltre la soglia
// (con margine) l'invio parte senza keepalive: perde solo la garanzia di
// sopravvivere all'unload, ma non fallisce con TypeError.
const KEEPALIVE_MAX_BODY = 60_000
const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308])

export interface TransportOptions {
  token: string
  stats: Stats
  fetchImpl?: FetchImpl | undefined
  maxQueue?: number
  timeoutMs?: number
  debug?: (message: string, error?: unknown) => void
}

export class Transport {
  private readonly token: string
  private readonly stats: Stats
  private readonly fetchImpl: FetchImpl | undefined
  private readonly maxQueue: number
  private readonly timeoutMs: number
  private readonly debug: ((message: string, error?: unknown) => void) | undefined

  private inFlight = 0
  private readonly pending = new Set<Promise<void>>()

  constructor(options: TransportOptions) {
    this.token = options.token
    this.stats = options.stats
    this.fetchImpl =
      options.fetchImpl ??
      (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined)
    this.maxQueue = options.maxQueue ?? 30
    this.timeoutMs = options.timeoutMs ?? 5000
    this.debug = options.debug
  }

  /// Invia [payload] (oggetto o array) a [url]. Ritorna `true` se l'invio è stato
  /// accettato in coda, `false` se scartato (coda piena o fetch assente).
  send(payload: unknown, url: string): boolean {
    const fetchImpl = this.fetchImpl
    if (!fetchImpl) {
      this.stats.increment('failed')
      return false
    }
    if (this.inFlight >= this.maxQueue) {
      this.stats.increment('dropped')
      this.debug?.('coda piena, evento scartato')
      return false
    }

    const body = JSON.stringify(payload)
    const controller = createAbortController()
    const timer =
      controller && this.timeoutMs > 0
        ? setTimeout(() => controller.abort(), this.timeoutMs)
        : undefined
    const init: RequestInit = {
      method: 'POST',
      headers: this.headers(),
      body,
      ...(isBrowser() && body.length < KEEPALIVE_MAX_BODY ? { keepalive: true } : {}),
      ...(isNode() ? { redirect: 'manual' as RequestRedirect } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    }

    this.inFlight += 1
    const promise = this.perform(fetchImpl, url, init)
      .catch((error: unknown) => {
        this.stats.increment('failed')
        this.debug?.('invio fallito', error)
      })
      .finally(() => {
        this.inFlight -= 1
        if (timer !== undefined) clearTimeout(timer)
        this.pending.delete(promise)
      })

    this.pending.add(promise)
    return true
  }

  /// Attende gli invii in volo (best-effort; non solleva).
  async flush(): Promise<void> {
    await Promise.allSettled([...this.pending])
  }

  // Esegue la richiesta seguendo a mano i redirect su Node (POST preservato).
  private async perform(
    fetchImpl: FetchImpl,
    url: string,
    init: RequestInit,
  ): Promise<void> {
    let currentUrl = url
    let response = await fetchImpl(currentUrl, init)

    if (isNode()) {
      let redirects = 0
      while (REDIRECT_STATUSES.has(response.status) && redirects < MAX_REDIRECTS) {
        const location = response.headers?.get('location')
        if (!location) break
        const nextUrl = new URL(location, currentUrl).toString()
        // Il follow ri-invia Authorization: consentito SOLO verso lo stesso
        // host o la variante www (il caso reale apex→www). Un Location verso
        // un host estraneo esfiltrerebbe il Bearer → stop.
        if (!allowedRedirectTarget(currentUrl, nextUrl)) break
        currentUrl = nextUrl
        redirects += 1
        response = await fetchImpl(currentUrl, init)
      }
    }

    if (response.status >= 200 && response.status < 300) {
      this.stats.increment('sent')
    } else {
      this.stats.increment('failed')
      this.debug?.(`invio fallito con stato ${response.status}`)
    }
  }

  private headers(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.token}`,
      'Content-Type': 'application/json',
      // Nel browser lo User-Agent è un forbidden header (lo imposta il browser).
      ...(isNode() ? { 'User-Agent': `${SDK_NAME}/${SDK_VERSION}` } : {}),
    }
  }
}

function createAbortController(): AbortController | undefined {
  return typeof AbortController === 'function' ? new AbortController() : undefined
}

// Stesso host oppure www-swap (`closeyour.it` ⇄ `www.closeyour.it`).
function allowedRedirectTarget(fromUrl: string, toUrl: string): boolean {
  try {
    const from = new URL(fromUrl).host
    const to = new URL(toUrl).host
    return from === to || `www.${from}` === to || from === `www.${to}`
  } catch {
    return false
  }
}
